import { getSampleLoop } from './sampleLibrary';

// A sampler that can hold a note for longer than the recording it plays from.
//
// Tone.Sampler plays each note as a one-shot: it starts the buffer and lets it run out, which is
// right for a piano and wrong for anything that sustains. Every file in the FluidR3 set is the
// same fixed-length render, so a held organ, string or pad note goes silent about three seconds
// in — at full level, since the recording was cut rather than faded. There is no way to ask
// Tone.Sampler to loop, so the parts of it that matter are done here instead: pick the closest
// recording, repitch it, and play it through a looping Tone.ToneBufferSource. sampleLibrary has
// already built the loop into those buffers and says where it runs.
//
// The surface is only what useMidiPlayer asks of a voice — connect, volume, triggerAttackRelease,
// releaseAll, dispose — so the two kinds of sampler are interchangeable at the call site.

const A4_MIDI = 69;
const A4_HZ = 440;

// Fractional, unlike Tone's own ftom, which rounds: the fraction is exactly the repitching a note
// between two recordings needs
const toMidi = (frequency) => A4_MIDI + 12 * Math.log2(frequency / A4_HZ);

const semitonesToRate = (semitones) => 2 ** (semitones / 12);

/**
 * @param {object} Tone - The loaded Tone module
 * @param {Record<string, object>} buffers - note name to ToneAudioBuffer, from sampleLibrary
 * @param {number} release - Seconds a note takes to fade once it is let go
 */
export function createLoopingSampler(Tone, buffers, { release = 1 } = {}) {
  // Sorted only so the set reads in pitch order while debugging — the search below is linear over
  // the twenty-odd recordings an instrument has, which is cheaper than anything cleverer
  const samples = Object.entries(buffers)
    .map(([note, buffer]) => ({ midi: Tone.Frequency(note).toMidi(), buffer }))
    .sort((a, b) => a.midi - b.midi);

  const output = new Tone.Volume();

  // Held so a stop can reach a note that is still sounding, and so dispose can take the lot down.
  // Each source drops itself as it ends, or a long clip would accumulate every note it ever played.
  let sources = [];

  /** The recording nearest a note, which repitching has to cover at most half a step of. */
  const findSample = (midi) =>
    samples.reduce((closest, sample) =>
      Math.abs(sample.midi - midi) < Math.abs(closest.midi - midi) ? sample : closest,
    );

  const triggerAttackRelease = (frequency, duration, time, velocity = 1) => {
    const midi = toMidi(frequency);
    const { midi: sampledMidi, buffer } = findSample(midi);
    const loop = getSampleLoop(buffer);

    const source = new Tone.ToneBufferSource({
      url: buffer,
      playbackRate: semitonesToRate(midi - sampledMidi),
      // Matching Tone.Sampler: no attack shaping, and the release as the note's own fade out
      fadeOut: release,
      curve: 'exponential',
      // Loop points are positions in the recording, so repitching leaves them alone
      loop: Boolean(loop),
      loopStart: loop ? loop.start : 0,
      loopEnd: loop ? loop.end : 0,
    }).connect(output);

    source.onended = () => {
      sources = sources.filter((playing) => playing !== source);
    };

    // Starting from 0 rather than the loop point plays the attack of the note first, and the
    // duration is what schedules the release — the source loops until then however long it is
    source.start(time, 0, duration, velocity);

    sources.push(source);
  };

  const voice = {
    volume: output.volume,

    connect: (destination) => {
      output.connect(destination);

      return voice;
    },

    triggerAttackRelease,

    releaseAll: (time) => {
      sources.forEach((source) => source.stop(time));
      sources = [];
    },

    dispose: () => {
      sources.forEach((source) => source.dispose());
      sources = [];
      output.dispose();
    },
  };

  return voice;
}
